import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from 'src/common/utils/api';
import { FundDocument, FundReportType } from '../types';
import { PickedItem } from 'src/data-capture/file-management/drop-zone/SharePointButton';

type OneDriveFundDocumentRequest = {
  fileName: string;
  downloadUrl: string;
  fileSize?: number;
  reportType: FundReportType;
  reportDate: string;
  isLatest: boolean;
};

function toISOString(date: Date | string): string {
  if (typeof date === 'string') {
    return new Date(date).toISOString();
  }
  return date.toISOString();
}

export const useOneDriveFundDocumentUpload = () => {
  const [isUploading, setIsUploading] = useState(false);
  const queryClient = useQueryClient();

  const uploadOneDriveFundDocument = async (
    fundId: string,
    item: PickedItem,
    metadata: {
      reportType: FundReportType;
      reportDate: Date | string;
      isLatest: boolean;
    },
  ): Promise<FundDocument> => {
    if (!item.name || !item.downloadUrl) {
      throw new Error('Invalid OneDrive item: missing name or downloadUrl');
    }

    try {
      setIsUploading(true);

      const request: OneDriveFundDocumentRequest = {
        fileName: item.name,
        downloadUrl: item.downloadUrl,
        fileSize: item.size,
        reportType: metadata.reportType,
        reportDate: toISOString(metadata.reportDate),
        isLatest: metadata.isLatest,
      };

      const response = await api.post<FundDocument>(`funds/${fundId}/files/onedrive`, request);

      await queryClient.invalidateQueries({ queryKey: ['funds', fundId, 'files'] });

      return response.data;
    } finally {
      setIsUploading(false);
    }
  };

  return {
    uploadOneDriveFundDocument,
    isUploading,
  };
};
